import { combineEpics } from 'redux-observable';
import { concat, EMPTY, forkJoin, from, of, pipe, throwError } from 'rxjs';
import { RootEpic } from 'app/app.epics.type';
import { filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { paymentSlice } from 'logic/store/payment/payment.slice';
import { Feature } from 'logic/store/authentication/feature/authorization.feature';
import { ofWithStripe } from 'logic/stripe';
import { fillReduxForm } from 'logic/redux-form/fill-redux-form';
import {
  BILLING_INFO_FORM_NAME,
  BillingInfoFormFields,
  BillingInfoFormValues,
  DEFAULT_VAT_ID_TYPE,
} from 'src/pages-all/tutoring-institute/components';
import { getFormValues } from 'redux-form';
import { isNotValidVatNumberError } from 'src/logic/error/not-valid-vat-number.error';
import { Dispatch } from 'redux';
import { translate, translationKeys } from 'logic/translations/translations.service';
import {
  PayBySEPAContactFormFields,
  SEPA_CONTACT_FORM_NAME,
} from 'src/pages-all/tutoring-institute/components/add-payment-method.modal/pay-by-sepa/SEPA-contact.form';
import { cancelSubscriptionEpics$ } from './cancel-subscription/cancel-subscription.epic';
import { PaymentCollectionMethod } from 'logic/api-models/payments';
import { StoreState } from 'app/app.reducers';
import { PaymentApi } from 'logic/store/payment/payment.api';
import { logError } from 'logic/log/log.logic';
import { instituteSlice } from 'logic/store/institute';

const { updateSyncErrors } = require('redux-form/lib/actions').default;

const fetchCustomer$: RootEpic = (
  action$,
  _,
  { ofIsAuthorized, ofIsAuthenticated, managed, paymentApi }
): any =>
  action$.pipe(
    filter(paymentSlice.actions.fetchCustomer.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.fetchCustomer,
      pipe(
        switchMap(() =>
          // need to nest to prevent TS errors
          forkJoin([
            forkJoin([
              from(paymentApi.getPaymentMethods()),
              from(paymentApi.getBillingInfo()),
              from(paymentApi.getSubscriptions()),
              from(paymentApi.getConfig()),
              from(paymentApi.getNextInvoice()),
              from(paymentApi.getDiscount()),
            ]),
            from(paymentApi.getInvoices()),
          ])
        ),
        mergeMap(
          ([
            [
              paymentMethodsRes,
              billingInfoRes,
              subscriptionsRes,
              configRes,
              nextInvoiceRes,
              discountRes,
            ],
            invoices,
          ]) =>
            of(
              paymentSlice.actions.setPaymentMethods(paymentMethodsRes.data?.data),
              paymentSlice.actions.setBillingInfo(billingInfoRes?.data?.data),
              paymentSlice.actions.setSubscriptions(subscriptionsRes.data?.data),
              paymentSlice.actions.setConfig(configRes.data?.data),
              paymentSlice.actions.setNextInvoice(nextInvoiceRes.data?.data),
              paymentSlice.actions.setDiscount(discountRes.data?.data),
              paymentSlice.actions.setInvoices(invoices.data?.data)
            )
        )
      )
    )
  );

const setupStripeCardPaymentMethod$: RootEpic = (
  action$,
  state$,
  { ofIsAuthorized, ofIsAuthenticated, managed, paymentApi }
): any =>
  action$.pipe(
    filter(paymentSlice.actions.setUpCardPaymentMethod.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.setUpCardPaymentMethod,
      pipe(
        ofWithStripe(),
        switchMap(([{ payload: cardElement }, stripe]) =>
          of(cardElement).pipe(
            switchMap(() => from(stripe.createPaymentMethod({ type: 'card', card: cardElement }))),
            switchMap(() => from(paymentApi.setUpIntent())),
            switchMap((res) => {
              const clientSecret = res.data.data?.clientSecret;

              if (!clientSecret) {
                throw new Error();
              }

              return from(
                stripe.confirmCardSetup(clientSecret, {
                  payment_method: {
                    card: cardElement,
                  },
                })
              );
            }),
            switchMap(({ error }) =>
              error ? throwError(error) : from(paymentApi.getPaymentMethods())
            ),
            withLatestFrom(state$),
            switchMap(([res, state]) =>
              updatePaymentSetup({
                id: res.data.data?.cards?.[0]?.id, // save newest card as default one
                collectionMethod: 'charge_automatically',
                paymentApi,
                state,
                isInvoice: false,
              })
            ),
            mergeMap(() =>
              of(
                paymentSlice.actions.closeAddPaymentMethodModal(),
                paymentSlice.actions.fetchCustomer()
              )
            )
          )
        )
      )
    )
  );

const setupStripeSEPAIbanPaymentMethod$: RootEpic = (
  action$,
  state$,
  { ofIsAuthorized, ofIsAuthenticated, managed, paymentApi }
): any =>
  action$.pipe(
    filter(paymentSlice.actions.setUpIbanSEPAPaymentMethod.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.setUpIbanSEPAPaymentMethod,
      pipe(
        ofWithStripe(),
        switchMap(([{ payload: ibanElement }, stripe]) =>
          of(ibanElement).pipe(
            withLatestFrom(state$),
            switchMap(() => from(paymentApi.setUpIntent())),
            withLatestFrom(state$),
            switchMap(([res, state]) => {
              const clientSecret = res.data.data?.clientSecret;

              if (!clientSecret) {
                throw new Error();
              }

              return from(
                stripe.confirmSepaDebitSetup(clientSecret, {
                  payment_method: {
                    sepa_debit: ibanElement,
                    billing_details: {
                      name: getFormValues(SEPA_CONTACT_FORM_NAME)(state)[
                        PayBySEPAContactFormFields.name
                      ],
                      email: getFormValues(SEPA_CONTACT_FORM_NAME)(state)[
                        PayBySEPAContactFormFields.email
                      ],
                    },
                  },
                })
              );
            }),
            switchMap(({ error }) =>
              error ? throwError(error) : from(paymentApi.getPaymentMethods())
            ),
            withLatestFrom(state$),
            switchMap(([res, state]) =>
              updatePaymentSetup({
                id: res.data.data?.sepa?.[0]?.id, // save newest SEPA as default method
                collectionMethod: 'charge_automatically',
                paymentApi,
                state,
                isInvoice: false,
              })
            ),
            mergeMap(() =>
              of(
                paymentSlice.actions.closeAddPaymentMethodModal(),
                paymentSlice.actions.fetchCustomer()
              )
            )
          )
        )
      )
    )
  );

const openBillingInfoModal$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(paymentSlice.actions.openBillingInfoModal.match),
    withLatestFrom(state$),
    map(([_, state]) =>
      fillReduxForm(BILLING_INFO_FORM_NAME, (data) => data, state.payment.billingInfo)
    )
  );

const updateBillingInfo$: RootEpic = (
  action$,
  state$,
  { managed, paymentApi, ofIsAuthenticated, ofIsAuthorized, dispatch }
) =>
  action$.pipe(
    filter(paymentSlice.actions.updateBillingInfo.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.updateBillingInfo,
      pipe(
        withLatestFrom(state$),
        map(
          ([_, stateLatestValue]) =>
            getFormValues(BILLING_INFO_FORM_NAME)(stateLatestValue) as BillingInfoFormValues
        ),
        switchMap((formData) =>
          from(
            paymentApi.updateBillingInfo({
              ...formData,
              [BillingInfoFormFields.VAT_ID]: formData['taxIdValue']
                ? DEFAULT_VAT_ID_TYPE
                : undefined,
              [BillingInfoFormFields.DefaultPaymentMethod]:
                formData['defaultPaymentMethod'] || undefined,
            })
          )
        )
      ),
      [handleInvalidVatNumberError(dispatch)]
    ),
    withLatestFrom(state$),
    switchMap(([_, state]) =>
      concat(
        of(
          paymentSlice.actions.setBillingInfo(
            getFormValues(BILLING_INFO_FORM_NAME)(state) as BillingInfoFormValues
          )
        ),
        of(paymentSlice.actions.closeBillingInfoModal())
      )
    )
  );

const handleInvalidVatNumberError = (dispatch: Dispatch) => (error: any) => {
  if (isNotValidVatNumberError(error)) {
    dispatch(
      updateSyncErrors(
        BILLING_INFO_FORM_NAME,
        {
          [BillingInfoFormFields.VAT]: translate(
            translationKeys.errors.invalidVatNumberForSelectedCountry
          ),
        },
        ''
      )
    );

    return true;
  }

  return false;
};

const setUpInvoicePaymentMethod$: RootEpic = (
  action$,
  state$,
  { ofIsAuthorized, ofIsAuthenticated, managed, paymentApi }
) =>
  action$.pipe(
    filter(paymentSlice.actions.setUpInvoicePaymentMethod.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.setUpInvoicePaymentMethod,
      pipe(
        switchMap(({ payload }) => from(paymentApi.updateInvoicePaymentSettings(payload))),
        mergeMap(() =>
          of(
            paymentSlice.actions.closeAddPaymentMethodModal(),
            paymentSlice.actions.fetchCustomer()
          )
        )
      )
    )
  );

const setUpNewSubscription$: RootEpic = (
  action$,
  state$,
  { paymentApi, managed, ofIsAuthenticated, ofIsAuthorized }
) =>
  action$.pipe(
    filter(paymentSlice.actions.setUpNewSubscription.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.setUpNewSubscription,
      pipe(
        withLatestFrom(state$),
        switchMap(([_, state]) =>
          !!state.payment?.config?.availablePrices?.[0]?.id
            ? from(
                paymentApi.setUpNewSubscription({
                  priceId: state.payment.config.availablePrices[0].id,
                  collectionMethod: state.payment?.paymentMethods?.invoice?.isDefault
                    ? 'send_invoice'
                    : 'charge_automatically',
                })
              )
            : tap(() => logError(translate(translationKeys.errors.contactSupport)))
        ),
        withLatestFrom(state$),
        switchMap(([_, state]) =>
          concat(
            state.payment.confirmPaymentStartModalOpened
              ? of(paymentSlice.actions.closeConfirmPaymentStartModal())
              : EMPTY,
            state.payment.offerIdPendingActivation
              ? of(
                  instituteSlice.actions.toggleInstituteOfferActive({
                    id: state.payment.offerIdPendingActivation,
                    isActive: true,
                  })
                )
              : EMPTY
          )
        )
      )
    )
  );

const updateStandardPaymentMethod$: RootEpic = (
  action$,
  state$,
  { ofIsAuthorized, ofIsAuthenticated, managed, paymentApi }
) =>
  action$.pipe(
    filter(paymentSlice.actions.updateStandardPaymentMethod.match),
    ofIsAuthenticated(),
    ofIsAuthorized(Feature.Institute),
    managed(
      paymentSlice.actions.updateStandardPaymentMethod,
      pipe(
        withLatestFrom(state$),
        switchMap(([{ payload: { isInvoice, collectionMethod, id } }, state]) =>
          updatePaymentSetup({
            isInvoice,
            collectionMethod,
            state,
            id,
            paymentApi,
          }).pipe(map(() => paymentSlice.actions.fetchCustomer()))
        )
      )
    )
  );

const updatePaymentSetup = (props: {
  id?: string;
  collectionMethod?: PaymentCollectionMethod;
  isInvoice?: boolean;
  state: StoreState;
  paymentApi: PaymentApi;
}) =>
  forkJoin(
    [
      props.id && from(props.paymentApi.updateStandardPaymentMethod(props.id)),
      props.state.payment.subscriptions &&
        props.collectionMethod &&
        from(props.paymentApi.updatePaymentCollectionMethod(props.collectionMethod)),
      from(props.paymentApi.updateInvoicePaymentSettings({ isDefault: props.isInvoice || false })),
    ].filter(Boolean)
  );

export const paymentEpics$ = combineEpics(
  fetchCustomer$,
  setupStripeCardPaymentMethod$,
  openBillingInfoModal$,
  updateBillingInfo$,
  setupStripeSEPAIbanPaymentMethod$,
  cancelSubscriptionEpics$,
  updateStandardPaymentMethod$,
  setUpInvoicePaymentMethod$,
  setUpNewSubscription$
);
